import React, { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { PublicMoney } from '@guest-widgets/shared/src/types/product/miscellaneous';
import { useLocalStorage } from '@guest-widgets/shared/src/hooks/useLocalStorage';

import { cartId } from '../cart/session/useSession';

export interface PaymentGiftCard {
  code: string;
  amount: PublicMoney;
}

export interface CheckoutContext {
  paymentId: string | undefined;
  setPaymentId: (paymentReference: string) => void;

  bookingLink: string | undefined;
  setBookingLink: (bookingLink: string) => void;

  giftCards: PaymentGiftCard[];
  addGiftCard: (giftCard: PaymentGiftCard) => void;
  removeGiftCard: (giftCard: PaymentGiftCard) => void;
  resetGiftCards: () => void;
}

const checkoutContext = createContext({} as CheckoutContext);

export const CheckoutContextProvider = ({ children }: PropsWithChildren<any>) => {
  const [bookingLink, setBookingLink] = useState<CheckoutContext['bookingLink']>();
  const [paymentId, setPaymentId] = useState<CheckoutContext['paymentId']>();

  const [giftCardsFromLocalStorage, setGiftCardsToLocalStorage] = useLocalStorage<
    PaymentGiftCard[]
  >(`${cartId}-giftCards`, []);

  const [giftCards, setGiftCards] = useState<PaymentGiftCard[]>(giftCardsFromLocalStorage ?? []);

  const addGiftCard = (newGiftCard: PaymentGiftCard) => {
    setGiftCards((giftCards) => {
      const exists = giftCards.filter((giftCard) => giftCard.code === newGiftCard.code).length > 0;
      return exists ? giftCards : [...giftCards, newGiftCard];
    });
  };

  const removeGiftCard = (giftCardToRemove: PaymentGiftCard) =>
    setGiftCards((giftCards) =>
      giftCards.filter((giftCard) => giftCard.code !== giftCardToRemove.code)
    );

  const resetGiftCards = () => {
    setGiftCards([]);
    setGiftCardsToLocalStorage([]);
  };

  useEffect(() => {
    setGiftCardsToLocalStorage(giftCards);
  }, [giftCards]);

  const contextData = {
    bookingLink,
    setBookingLink,
    paymentId,
    setPaymentId,
    giftCards,
    addGiftCard,
    removeGiftCard,
    resetGiftCards,
  };

  return <checkoutContext.Provider value={contextData}>{children}</checkoutContext.Provider>;
};

export const useCheckout = () => useContext(checkoutContext);
